/**
 * Service Health Card Component
 * Displays individual service health status with latency metrics
 */

import { Card } from '@/components/ui/Card';
import { CheckCircle, XCircle, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { ServiceHealth } from '@/types/monitoring';

export interface ServiceHealthCardProps {
  service: ServiceHealth;
}

/**
 * Get status icon, colors and label key based on service health status
 */
function getStatusDisplay(status: ServiceHealth['status']) {
  switch (status) {
    case 'healthy':
      return {
        icon: CheckCircle,
        color: 'text-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        labelKey: 'healthy' as const,
      };
    case 'unhealthy':
      return {
        icon: AlertCircle,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        labelKey: 'unhealthy' as const,
      };
    case 'error':
      return {
        icon: XCircle,
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        labelKey: 'error' as const,
      };
    default:
      return {
        icon: AlertCircle,
        color: 'text-gray-500',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        labelKey: 'unknown' as const,
      };
  }
}

/**
 * Format latency for display
 */
function formatLatency(latency: number | null): string {
  if (latency === null) return 'N/A';
  if (latency < 1) return '<1ms';
  return `${Math.round(latency)}ms`;
}

export function ServiceHealthCard({ service }: ServiceHealthCardProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.serviceHealth;

  const statusDisplay = getStatusDisplay(service.status);
  const StatusIcon = statusDisplay.icon;
  const statusLabel = t[statusDisplay.labelKey];

  return (
    <Card className={`${statusDisplay.bgColor} border ${statusDisplay.borderColor}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${statusDisplay.color}`} />
          <h3 className="font-medium text-gray-900">{service.name}</h3>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${statusDisplay.bgColor} ${statusDisplay.color} font-medium`}>
          {statusLabel}
        </span>
      </div>

      {/* Latency */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <Clock className="w-4 h-4" />
        <span>{t.latency}: {formatLatency(service.latency_ms)}</span>
      </div>

      {/* Error Message */}
      {service.error && (
        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700">
          <strong>{t.error}:</strong> {service.error}
        </div>
      )}

      {/* External URL */}
      {service.external_url && (
        <div className="mt-2">
          <a
            href={service.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            <span>{t.openService}</span>
          </a>
        </div>
      )}
    </Card>
  );
}
